<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6>
        {{ `${$t('fields.result')} (${requests.length === selectedLimit ? requests.length + "+" : requests.length})` }}
      </h6>
      <b-button v-if="type==='deposit' && $allowPermission('agent:manual.deposit')" variant="success" @click="onToggleTopupManual({})">
        ฝากมือ
      </b-button>
      <h6 v-if="type==='withdraw'">
        หากขึ้นข้อความว่า “<span class="text-danger">เกิดข้อผิดพลาดจาก SCB</span>” กรุณารอสักครู่ 5-10 นาทีแล้วทำรายการใหม่อีกครั้ง
      </h6>
    </div>
    <b-row class="mb-3">
      <b-col>
        <LimitSelect v-model="selectedLimit"/>
        <span v-if="isFetching" class="text-muted">กำลังโหลด...</span>
      </b-col>
      <b-col v-if="isOwner" cols="3">
        <MasterSelectInput 
          :value="selectedMaster"
          hide-label
          @update="onMasterIdChange"
        />
      </b-col>
      <b-col v-if="isOwner || isMaster" cols="3">
        <AgentsSelectInput
          :master-id="selectedMaster"
          :value="selectedAgent"
          hide-label
          @update="onAgentIdChange"
        />
      </b-col>
    </b-row>
    <!-- Table -->
    <b-table
      :fields="type==='deposit'? depositFields: withdrawFields"
      :items="requests"
      responsive
      show-empty
    >
      <template #cell(#)="data">
        {{ data.index + 1 }}
      </template>
      <template #cell(transactionDateTime)="data">
        {{ data.value | datetime }}
      </template>
      <template #cell(createdAt)="data">
        {{ data.value | datetime }}
      </template>
      <template #cell(fromAccountNumber)="data">
        <b-media vertical-align="center">
          <template #aside>
            <BankIcon :bank-code="data.item.fromBankCode"/>
          </template>
          {{ data.item.fromAccountNumber }}<br>
          {{ data.item.fromAccountName}}
        </b-media>
      </template>
      <template #cell(userUsername)="data">
        <component :is="$allowPermission('agent:manage.user') && type==='withdraw' && data.item.userId ? 'a' : 'span'" :href="`/players/detail/${data.item.userId}`" target="_blank">
          <b-media vertical-align="center">
            {{ data.value.substring(0, 3) + 'xxx' + data.value.substring(6, 10) }}<br>
          </b-media>
        </component>
      </template>
      <template #cell(toBankAccountNumber)="data">
        <component :is="$allowPermission('agent:manage.user') && type==='withdraw' && data.item.userId ? 'a' : 'span'" :href="`/players/detail/${data.item.userId}`" target="_blank">
          <b-media vertical-align="center">
            <template #aside>
              <BankIcon :bank-code="data.item.toBankCode"/>
            </template>
            {{ data.value }}<br>
            {{ data.item.userFirstname }} {{ data.item.userLastname }}
          </b-media>
        </component>
      </template>
      <template #cell(toAccountNumber)="data">
        <b-media vertical-align="center">
          <template #aside>
            <BankIcon :bank-code="data.item.toBankCode"/>
          </template>
          {{ data.value }}<br>
          {{ data.item.toAccountName}}
        </b-media>
      </template>
       <template #cell(transactionChannelCode)="data">
        <b-badge>
          <template v-if="data.item.transactionChannelDescription">
            {{ data.item.transactionChannelDescription || '-' }}
            ({{ data.value || '-' }})
          </template>
          <template v-else>
            {{ data.value || '-' }}
          </template>
        </b-badge>
      </template>
      <template #cell(afterBalance)="data">
        <b-badge class="badge-soft-danger">
          {{ data.item.beforeBalance | currency }}
        </b-badge><br>
        <b-badge class="badge-soft-success">
          {{ data.value | currency }}
        </b-badge>
      </template>
      <template #cell(remain)="data">
        {{ displayRemainAmount(data.item) }}
      </template>
      <template #cell(amount)="data">
        <span>{{ data.value | currency }}</span>
        <div v-if="data.item.withdrawFrom && data.item.withdrawFrom === 'cashback'" class="text-muted small">
          (ถอนจากเงินคืน)
        </div>
      </template>
      
      <template #cell(operation)="data">
        <b-button-group size="sm">
          <b-button
            variant="info"
            @click="onAdjustDeposit(data.item)"
          >
            ปรับยอด
          </b-button>
          <b-button
            variant="danger"
            @click="onRemoveDeposit(data.item.id)"
          >
           {{ $t('buttons.remove') }}
          </b-button>
        </b-button-group>
      </template>
      <template #cell(operation2)="data">
        <b-button-group size="sm">
          <b-button
            variant="info"
            @click="onApproveWithdraw(data.item, 'auto')"
          >
            ถอนออโต้
          </b-button>
          <b-button variant="warning" @click="onApproveWithdraw(data.item, 'manual')">
           ถอนมือ
          </b-button>
           <b-button
            variant="danger"
            @click="onRejectPendingCredit(data.item.id)"
          >
           ปฎิเสธ
          </b-button>
        </b-button-group><br>
        <b-button-group size="sm">
           <b-button
            variant="light"
            @click="$router.push(`/credits/all?search=${data.item.userUsername}`)"
          >
            รายงานเครดิต
          </b-button>
          <b-button
            variant="dark"
           @click="onHide(data.item.id)"
          >
           ซ่อนรายการ
          </b-button>
        </b-button-group>
      </template>
      <template #empty="">
        <p class="text-center text-muted">{{ $t('messages.nothing_here') }}</p>
      </template>
    </b-table>
    <!-- pagination -->
    <SimplePagination :has-more-page="pagination.itemCount >= selectedLimit" :per-page="limit" @update="(val) => currentPage = val"/>

    <unmatched-deposit-modal :txn="selectedTxn" />
    <approve-withdraw-modal v-if="type === 'withdraw'" :approve-type="approveType" :txn="selectedTxn"></approve-withdraw-modal>
    <top-up-manual-modal v-if="type === 'deposit'" :txn="selectedTxn"></top-up-manual-modal>
    <reject-withdraw-modal :txn-id="selectedTxnId"></reject-withdraw-modal>
  </b-card>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'

export default {
  name: 'PendingTransactions',
  components: {
    SimplePagination: () => import('@components/commons/simple-pagination'),
    MasterSelectInput: () =>
      import('@components/master/master-select-input'),
    AgentsSelectInput: () =>
      import('@components/agents/agents-select-input'),
    UnmatchedDepositModal: () =>
      import('@components/transactions/unmatched-deposit.vue'),
    ApproveWithdrawModal: () =>
        import('@components/transactions/approve-withdraw-modal.vue'),
    TopUpManualModal: () =>
        import('@components/transactions/topup-manual-modal'),
    RejectWithdrawModal: () => import('@components/transactions/reject-withdraw.modal.vue'),
  },
  props: {
    type: {
      type: String,
      default: 'deposit',
    },
  },
  data() {
    return {
      currentPage: 1,
      selectedLimit: 20,
      selectedTxn: {},
      depositFields: [
        '#',
        {
          key: 'transactionChannelCode',
          label: 'ช่องทาง',
        },
        {
          key: 'fromAccountNumber',
          label: 'บัญชีโอน',
        },
        {
          key: 'toAccountNumber',
          label: 'บัญชีรับ',
        },
        {
          key: 'amount',
          label: this.$t('credits.credit_amount'),
          class: 'text-right',
        },
        {
          key: 'afterBalance',
          label: 'คงเหลือ (ก่อน-หลัง)',
          class: 'text-right',
        },
        {
          key: 'transactionDateTime',
          label: 'เวลาฝาก',
        },
        {
          key: 'operation',
          label: `${this.$t('buttons.operation')}`,
          class: 'text-right',
        },
      ],
      withdrawFields: [
        '#',
        {
          key: 'userUsername',
          label: 'ยูสเซอร์เนม',
        },
        {
          key: 'toBankAccountNumber',
          label: 'บัญชี'
        },
        {
          key: 'amount',
          label: this.$t('credits.credit_amount'),
          class: 'text-right',
        },
        {
          key: 'remark',
          label: 'หมายเหตุ'
        },
        {
          key: 'createdAt',
          label: 'เวลาแจ้ง',
        },
        {
          key: 'operation2',
          label: `${this.$t('buttons.operation')}`,
          class: 'text-right',
        },
      ],
      rejectForm: { txnId: '', detail: '', isReturnCredit: false },
      approveType: 'adjust',
      selectedTxnId: '',
      selectedMaster: '',
      selectedAgent: '',
    }
  },
  computed: {
    ...mapState({
      isFetching: (state) => state.deposit.isFetchingDepositUnmatched || state.withdraw.isFetchingWithdraws,
      isRejecting: (state) => state.credit.isRejectingPlayerWithdraw,
      isRejectingSuccess: (state) =>
        state.credit.isRejectingPlayerWithdrawSuccess,
    }),
    ...mapGetters(['isOwner', 'isMaster', 'depositUnmatched', 'withdraws']),
    requests() {
      return this.type === 'deposit'? this.depositUnmatched.items || []: this.withdraws.items
    },
    pagination() {
      return this.type === 'deposit'? this.depositUnmatched.meta : this.withdraws.meta
    },
    limit() {
      return this.pagination.itemsPerPage
    },
  },
  watch: {
    currentPage(val) {
      if(val){
        this.$addPageToLocation(val)
        this.fetchData()
      }
    },
    selectedLimit(val) {
      if(val) {
        this.currentPage > 1 ? this.currentPage = 1 : this.fetchData()
      }
    },
  },
  methods: {
    ...mapActions([
      'fetchDepositUnmatched',
      'fetchWithdraws',
      'manualSetStatusToSuccess',
      'deleteDepositUnmatched',
      'hideWithdraw',
    ]),
    fetchData() {
      if(this.type === 'deposit'){
        this.fetchDepositUnmatched({
          limit: this.selectedLimit,
          page: this.currentPage,
          search: '',
          masterId: this.selectedMaster,
          agentId: this.selectedAgent,
        })
      } else {
        this.fetchWithdraws({
          limit: this.selectedLimit,
          page: this.currentPage,
          search: '',
          masterId: this.selectedMaster,
          agentId: this.selectedAgent,
        })
      }
    },
    onAgentIdChange(agentId) {
      this.selectedAgent = agentId
      this.$emit('on-agent-change', agentId)
      this.fetchData()
    },
    onMasterIdChange(masterId) {
      this.selectedMaster = masterId
      this.$emit('on-master-change', masterId)
      this.fetchData()
    },
    displayRemainAmount(txn) {
      const remain = (Number(txn.beforeBalance) - Number(txn.afterBalance));
      return remain.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    },
    onAdjustDeposit(txn) {
      this.$bvModal.show('unmatched-deposit-modal')
      this.selectedTxn = txn
    },
    onApproveWithdraw(txn, type) {
      this.$bvModal.show('approve-withdraw-modal')
      this.selectedTxn = txn
      this.approveType = type
    },
    onRejectPendingCredit(id) {
      this.$bvModal.show('reject-withdraw-modal')
      this.selectedTxnId = id
    },
    onHide(id){
      this.$bvModal
        .msgBoxConfirm(`${this.$t('messages.confirm')}`, {
          okTitle: `${this.$t('buttons.confirm')}`,
          cancelTitle: `${this.$t('buttons.cancel')}`,
        })
        .then((confirm) => {
          if (confirm) {
            this.hideWithdraw(id)
          }
        })
    },
    onToggleTopupManual(txn) {
      this.selectedTxn = txn
      this.$bvModal.show('topup-manual-modal')
    },
    onRemoveDeposit(id) {
      this.$bvModal
        .msgBoxConfirm(`${this.$t('messages.confirm')}`, {
          okTitle: `${this.$t('buttons.confirm')}`,
          cancelTitle: `${this.$t('buttons.cancel')}`,
        })
        .then((confirm) => {
          if (confirm) {
            this.deleteDepositUnmatched(id)
          }
        })
    },
  },
}
</script>
